import type { KonvaEventObject } from 'konva/lib/Node';
import { createRef, Fragment } from 'react';
import { Label, Tag, Text } from 'react-konva';

import CseMachine from '../CseMachine';
import { Config, ShapeDefaultProps } from '../CseMachineConfig';
import { ControlStashConfig } from '../CseMachineControlStashConfig';
import { Layout } from '../CseMachineLayout';
import type { IHoverable } from '../CseMachineTypes';
import {
  defaultActiveColor,
  defaultExprStmtColor,
  defaultStrokeColor,
  defaultTextColor,
  getTextHeight,
  setHoveredCursor,
  setUnhoveredCursor,
  truncateText,
} from '../CseMachineUtils';
import { ArrowFromControlItemComponent } from './arrows/ArrowFromControlItemComponent';
import { Frame } from './Frame';
import { Visible } from './Visible';

export class ControlItemComponent extends Visible implements IHoverable {
  /** text to display */
  readonly text: string;
  readonly tooltipRef: React.RefObject<any>;
  readonly arrow?: ArrowFromControlItemComponent;

  constructor(
    readonly value: any,
    /** text to display on hover */
    readonly tooltip: string,
    /** The height of the stack so far */
    stackHeight: number,
    /** callback function to highlight editor lines on hover */
    readonly highlightOnHover: () => void,
    /** callback function to unhighlight editor lines after hover */
    readonly unhighlightOnHover: () => void,
    readonly topItem: boolean,
    arrowTo?: Frame,
    /** true if this control item is an expression statement (py-slang#270) */
    readonly isExpressionStatement: boolean = false,
  ) {
    super();
    this.text = truncateText(
      String(value),
      ControlStashConfig.ControlMaxTextWidth,
      ControlStashConfig.ControlMaxTextHeight,
    );
    this.tooltipRef = createRef();
    this.highlightOnHover = highlightOnHover;
    this.unhighlightOnHover = unhighlightOnHover;
    this._x = ControlStashConfig.ControlPosX;
    this._y = ControlStashConfig.ControlPosY + stackHeight;
    this._width = ControlStashConfig.ControlItemWidth;
    this._height =
      getTextHeight(
        this.text,
        ControlStashConfig.ControlMaxTextWidth,
        `${ControlStashConfig.FontStyle} ${ControlStashConfig.FontSize}px ${ControlStashConfig.FontFamily}`,
        ControlStashConfig.FontSize,
      ) +
      ControlStashConfig.ControlItemTextPadding * 2;
    if (arrowTo) {
      this.arrow = new ArrowFromControlItemComponent(this).to(
        arrowTo,
      ) as ArrowFromControlItemComponent;
    }
  }

  // Save previous z-index to go back to later
  private zIndex = 1;
  onMouseEnter = (e: KonvaEventObject<MouseEvent>) => {
    this.highlightOnHover();
    if (!this.topItem) {
      // Not the generic setHoveredStyle() util — it hardcodes the "normal" colors it
      // restores to and doesn't know about isExpressionStatement, so use the
      // component's own (already-correct) highlight logic instead.
      this.setArrowSourceHighlightedStyle();
    }
    setHoveredCursor(e.currentTarget);
    this.zIndex = Math.max(this.ref.current.zIndex(), 1);
    this.ref.current.moveToTop();
    this.tooltipRef.current.moveToTop();
    this.tooltipRef.current.show();
  };

  onMouseLeave = (e: KonvaEventObject<MouseEvent>) => {
    this.unhighlightOnHover?.();
    setUnhoveredCursor(e.currentTarget);
    this.tooltipRef.current.hide();
    if (!this.topItem) {
      // See onMouseEnter — setUnhoveredStyle() resets Text fill to defaultTextColor()
      // unconditionally, which wiped the expression-statement color back to white on
      // unhover. setArrowSourceNormalStyle() restores textColor()/strokeColor() instead.
      this.setArrowSourceNormalStyle();
    }
    this.ref.current.zIndex(this.zIndex);
  };

  setArrowSourceHighlightedStyle(): void {
    this.tag?.stroke(Config.HoverColor);
    this.secItem?.fill(Config.HoverColor);
  }

  private strokeColor(): string {
    return this.topItem ? defaultActiveColor() : defaultStrokeColor();
  }

  // Expression statements (py-slang#270) are marked by the TEXT color, not the border —
  // the border already conveys "top of control" (active color) independently, and an
  // expression statement should stay visually flagged even while it's the top item, not
  // just once it's buried under other items.
  private textColor(): string {
    return this.isExpressionStatement ? defaultExprStmtColor() : defaultTextColor();
  }

  setArrowSourceNormalStyle(): void {
    this.tag?.stroke(this.strokeColor());
    this.secItem?.fill(this.textColor());
  }

  destroy() {
    this.ref.current.destroyChildren();
  }

  draw(): React.ReactNode {
    const textProps = {
      fill: this.textColor(),
      padding: ControlStashConfig.ControlItemTextPadding,
      fontFamily: ControlStashConfig.FontFamily,
      fontSize: ControlStashConfig.FontSize,
      fontStyle: ControlStashConfig.FontStyle,
      fontVariant: ControlStashConfig.FontVariant,
    };
    const tagProps = {
      stroke: this.strokeColor(),
      cornerRadius: ControlStashConfig.ControlItemCornerRadius,
    };
    return (
      <Fragment key={Layout.key++}>
        <Label
          ref={this.ref}
          x={this.x()}
          y={this.y()}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
        >
          <Tag {...ShapeDefaultProps} {...tagProps} />
          <Text
            {...ShapeDefaultProps}
            {...textProps}
            text={this.text}
            width={this.width()}
            height={this.height()}
          />
        </Label>
        <Label
          x={this.x() + this.width() + ControlStashConfig.TooltipMargin}
          y={this.y() + ControlStashConfig.TooltipMargin}
          visible={false}
          ref={this.tooltipRef}
        >
          <Tag
            {...ShapeDefaultProps}
            stroke={Config.HoverBgColor}
            fill={Config.HoverBgColor}
            opacity={ControlStashConfig.TooltipOpacity}
          />
          <Text
            {...ShapeDefaultProps}
            {...textProps}
            fill={CseMachine.getPrintableMode() ? Config.PrintBgColor : Config.TextColor}
            text={this.tooltip}
            padding={ControlStashConfig.TooltipPadding}
          />
        </Label>
        {this.arrow?.draw()}
      </Fragment>
    );
  }
}
